import { autoinject, bindingMode, TaskQueue } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { RouteConfig, NavigationInstruction } from 'aurelia-router';
import { IMemberSearchFilters, MemberService } from 'app/cd/services/member-service';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ICollapse } from 'app/shared/attributes/ess-collapse-attributes';
import { apiCall } from 'app/shared/lib/api/api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import * as CD from 'app/cd/models/essette-api'
import { guid } from 'app/shared/lib/guid';
import { EventAggregator } from 'aurelia-event-aggregator';
import { SetFocusEvent } from 'app/shared/lib/events';
import { requiredFields } from 'app/cd/elements/members/member-display';
import { NamespaceOptions } from 'app/shared/lib/i18n';

@autoinject()
export class PatientSearchPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////

    @bindable.boolean({ defaultBindingMode: bindingMode.oneTime })
    public defaultCollapsed: boolean = false;

    @bindable.boolean({ defaultBindingMode: bindingMode.oneTime })
    public defaultSearchCriteria: boolean = false;

    public memberSearchFilters: IMemberSearchFilters = {};
    public tempMemberSearchFilters: IMemberSearchFilters = {};

    protected memberResults: ReadonlyArray<CD.Member> = [];
    private hasSearchRoute: boolean = false;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 20;
    protected hasIneligibleMembers: boolean = false;

    @bindable.objectNull()
    public memberAvailableFilters = ['identifier.primary', 'identifier.medicaid', 'name.first', 'name.last', 'birthDate', 'primaryCarePhysician', 'primaryCarePhysician.name.first', 'primaryCarePhysician.name.last', 'healthPlan', 'lineOfBusiness', 'requestDate'];

    protected i18nNamespace: NamespaceOptions = NamespaceOptions.PP;

    protected id: guid = guid();

    ////****************** Element References ***************************************////
    protected $collapsible!: ICollapse;
    protected $searchCriteriaHeader!: HTMLElement;
    protected $currentCriteriaHeader!: HTMLElement;
    protected $pager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(private readonly memberService: MemberService,
        private readonly taskQueue: TaskQueue,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
    }

    protected async canActivate(params: any, routeConfig: RouteConfig, navInstruction: NavigationInstruction): Promise<boolean> {
        await this.checkPermissions();
        return this.hasSearchRoute;
    }

    protected attached(): void {
        this.taskQueue.queueTask(() => {
            this.showAvailableCriteria();
        });
    }


    ////****************** Events ***************************************************////
    protected handleSubmit(event?: Event): void {
        if(!this.state.saving && !this.state.loading) {
            this.search();
        }
    }

    protected handleKeyDown(e: KeyboardEvent): boolean {
        if(e.keyCode === 13 && e.ctrlKey) {
            this.handleSubmit();
            return false;
        }
        return true;
    }



    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasSearchRoute = this.accountContext.hasRouteAccess('pp.members.search')
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.searchMembers();
    }

    public clear(returnFocus?: boolean): void {
        this.memberSearchFilters = {};
        this.tempMemberSearchFilters = {};
        this.memberResults = [];
        this.resultCount = 0;
        if(this.$pager) this.$pager.updatePage(0);
        this.showAvailableCriteria();
        if(returnFocus) this.events.publish(new SetFocusEvent(this.$searchCriteriaHeader));
    }

    public showAvailableCriteria(): void {
        this.$collapsible.open(0);
        this.$collapsible.close(1);
    }

    public showSelectedCriteria(): void {
        this.$collapsible.close(0);
        this.$collapsible.open(1);
    }

    protected search(): void {
        this.searchMembers();
        this.showSelectedCriteria();
    }

    protected submitSearch() {
        this.memberSearchFilters = { ...this.tempMemberSearchFilters };
        this.search();
        this.events.publish(new SetFocusEvent(this.$currentCriteriaHeader));
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async searchMembers(): Promise<void> {
        if(!this.memberSearchFilters) {
            return;
        }

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting
        };

        const includes: string[] = [
            'gender',
            'primaryPlan.healthPlan',
            'primaryPlan.lineOfBusiness'
        ];

        const fields: string[] = [
            'eligibility.isCurrent',
            'birthDate',
            'primaryPlan.healthPlan.description',
            'primaryPlan.lineOfBusiness.description',
            'gender.description',
        ];
        requiredFields.forEach(field => !fields.includes(field) ? fields.push(field) : null);

        try {
            this.state.beginLoad();

            const response = await this.memberService.search(this.memberSearchFilters, options, includes, fields);

            this.memberResults = response.results;
            this.resultCount = response.count || 0;

            // If results include some ineligible members, show the warning column
            this.hasIneligibleMembers = this.memberResults.some(m => { return m.eligibility && !m.eligibility.isCurrent; });
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
